import { LightningStrike } from "@folio-2026/shared"
import { color, Fn, uniform } from "three/tsl"
import * as THREE from "three/webgpu"

export type StrikesSettings = {
  color?: THREE.Color
  emissiveColor?: THREE.Color
  emissiveIntensity?: number
  minSourceRadius?: number
  maxSourceRadius?: number
  minSourceHeight?: number
  maxSourceHeight?: number
  minDestRadius?: number
  maxDestRadius?: number
  minDestHeight?: number
  maxDestHeight?: number
  resetInterval?: number
  radius0?: number
  radius1?: number
  minRadius?: number
  maxIterations?: number
  isEternal?: boolean
  timeScale?: number
  propagationTimeFactor?: number
  vanishingTimeFactor?: number
  subrayPeriod?: number
  subrayDutyCycle?: number
  maxSubrayRecursion?: number
  ramification?: number
  recursionProbability?: number
  roughness?: number
  straightness?: number
}

type StrikesProps = {
  scene: THREE.Scene
  settings?: StrikesSettings
}

export default class Strikes {
  scene: THREE.Scene

  lightningStrike!: LightningStrike
  lightningStrikeMaterial!: THREE.MeshBasicNodeMaterial
  lightningStrikeMesh!: THREE.Mesh

  currentTime: number = 0
  resetTimerInterval: number | null = null
  resetTimerTimeout: number | null = null
  halfIntervalTimeout: number | null = null

  settings = {
    color: uniform(color("#b8b8b8")),
    emissiveColor: uniform(color("#2a70d8")),
    emissiveIntensity: uniform(0.8),

    minSourceRadius: 0.6,
    maxSourceRadius: 1.5,
    minSourceHeight: 0.5,
    maxSourceHeight: 4,

    minDestRadius: 0.5,
    maxDestRadius: 3,
    minDestHeight: 0.5,
    maxDestHeight: 2,

    resetInterval: 500, // milliseconds

    // Ray parameters
    sourceOffset: { x: 0, y: 0, z: 0 }, // Will be calculated
    destOffset: { x: 0, y: 0, z: 0 }, // Will be calculated
    radius0: 0.0,
    radius1: 0.02,
    minRadius: 0,
    maxIterations: 3,
    isEternal: true,
    timeScale: 1.7,
    propagationTimeFactor: 0.32,
    vanishingTimeFactor: 0.17,
    subrayPeriod: 1.3,
    subrayDutyCycle: 0.3,
    maxSubrayRecursion: 1,
    ramification: 5,
    recursionProbability: 0,
    roughness: 0.58,
    straightness: 0.8,
  }

  constructor({ scene, settings }: StrikesProps) {
    this.scene = scene

    // Merge provided settings with defaults
    if (settings) {
      if (settings.color) {
        this.settings.color = uniform(color(settings.color))
      }
      if (settings.emissiveColor) {
        this.settings.emissiveColor = uniform(color(settings.emissiveColor))
      }
      if (settings.emissiveIntensity !== undefined) {
        this.settings.emissiveIntensity = uniform(settings.emissiveIntensity)
      }
      if (settings.minSourceRadius !== undefined) {
        this.settings.minSourceRadius = settings.minSourceRadius
      }
      if (settings.maxSourceRadius !== undefined) {
        this.settings.maxSourceRadius = settings.maxSourceRadius
      }
      if (settings.minSourceHeight !== undefined) {
        this.settings.minSourceHeight = settings.minSourceHeight
      }
      if (settings.maxSourceHeight !== undefined) {
        this.settings.maxSourceHeight = settings.maxSourceHeight
      }
      if (settings.minDestRadius !== undefined) {
        this.settings.minDestRadius = settings.minDestRadius
      }
      if (settings.maxDestRadius !== undefined) {
        this.settings.maxDestRadius = settings.maxDestRadius
      }
      if (settings.minDestHeight !== undefined) {
        this.settings.minDestHeight = settings.minDestHeight
      }
      if (settings.maxDestHeight !== undefined) {
        this.settings.maxDestHeight = settings.maxDestHeight
      }
      if (settings.resetInterval !== undefined) {
        this.settings.resetInterval = settings.resetInterval
      }
      if (settings.radius0 !== undefined) {
        this.settings.radius0 = settings.radius0
      }
      if (settings.radius1 !== undefined) {
        this.settings.radius1 = settings.radius1
      }
      if (settings.minRadius !== undefined) {
        this.settings.minRadius = settings.minRadius
      }
      if (settings.maxIterations !== undefined) {
        this.settings.maxIterations = settings.maxIterations
      }
      if (settings.isEternal !== undefined) {
        this.settings.isEternal = settings.isEternal
      }
      if (settings.timeScale !== undefined) {
        this.settings.timeScale = settings.timeScale
      }
      if (settings.propagationTimeFactor !== undefined) {
        this.settings.propagationTimeFactor = settings.propagationTimeFactor
      }
      if (settings.vanishingTimeFactor !== undefined) {
        this.settings.vanishingTimeFactor = settings.vanishingTimeFactor
      }
      if (settings.subrayPeriod !== undefined) {
        this.settings.subrayPeriod = settings.subrayPeriod
      }
      if (settings.subrayDutyCycle !== undefined) {
        this.settings.subrayDutyCycle = settings.subrayDutyCycle
      }
      if (settings.maxSubrayRecursion !== undefined) {
        this.settings.maxSubrayRecursion = settings.maxSubrayRecursion
      }
      if (settings.ramification !== undefined) {
        this.settings.ramification = settings.ramification
      }
      if (settings.recursionProbability !== undefined) {
        this.settings.recursionProbability = settings.recursionProbability
      }
      if (settings.roughness !== undefined) {
        this.settings.roughness = settings.roughness
      }
      if (settings.straightness !== undefined) {
        this.settings.straightness = settings.straightness
      }
    }

    this.calculateSourceOffset()
    this.calculateDestOffset()
    this.createMesh()
    this.startResetTimer()
  }

  startResetTimer() {
    // Clear any existing timers
    if (this.resetTimerInterval !== null) {
      clearInterval(this.resetTimerInterval)
      this.resetTimerInterval = null
    }
    if (this.resetTimerTimeout !== null) {
      clearTimeout(this.resetTimerTimeout)
      this.resetTimerTimeout = null
    }
    if (this.halfIntervalTimeout !== null) {
      clearTimeout(this.halfIntervalTimeout)
      this.halfIntervalTimeout = null
    }

    // Calculate random delay between 0 and resetInterval
    const randomDelay = Math.random() * this.settings.resetInterval * 2

    // Start timer after random delay
    this.resetTimerTimeout = window.setTimeout(() => {
      // First reset after delay
      this.calculateSourceOffset()
      this.calculateDestOffset()
      this.recreateLightning()

      // Set up half-interval timer to double dest offset
      this.scheduleHalfInterval()

      // Then set up interval for subsequent resets
      this.resetTimerInterval = window.setInterval(() => {
        this.calculateSourceOffset()
        this.calculateDestOffset()
        this.recreateLightning()
        // Schedule next half-interval
        this.scheduleHalfInterval()
      }, this.settings.resetInterval)
    }, randomDelay)
  }

  scheduleHalfInterval() {
    // Clear any existing half-interval timer
    if (this.halfIntervalTimeout !== null) {
      clearTimeout(this.halfIntervalTimeout)
    }

    // Schedule timer to double dest offset at half interval
    this.halfIntervalTimeout = window.setTimeout(() => {
      this.doubleDestOffset()
    }, this.settings.resetInterval / 4)
  }

  doubleDestOffset() {
    // Calculate direction from source to current destination
    const sourceX = this.settings.sourceOffset.x
    const sourceY = this.settings.sourceOffset.y
    const sourceZ = this.settings.sourceOffset.z

    const destX = this.settings.destOffset.x
    const destY = this.settings.destOffset.y
    const destZ = this.settings.destOffset.z

    // Calculate vector from source to destination
    const direction = new THREE.Vector3(
      destX - sourceX,
      destY - sourceY,
      destZ - sourceZ,
    )

    // Double the distance while keeping the same direction
    direction.multiplyScalar(2)

    // Update destination offset
    this.settings.destOffset = {
      x: sourceX + direction.x,
      y: sourceY + direction.y,
      z: sourceZ + direction.z,
    }

    // Update the lightning strike
    this.recreateLightning()
  }

  calculateSourceOffset() {
    // Generate random angle (0 to 2π)
    const angle = Math.random() * Math.PI * 2

    // Generate random radius between minSourceRadius and maxSourceRadius
    const radius =
      this.settings.minSourceRadius +
      Math.random() *
        (this.settings.maxSourceRadius - this.settings.minSourceRadius)

    // Calculate x and y from angle and radius
    const x = radius * Math.cos(angle)
    const z = radius * Math.sin(angle)

    // Generate random z between minSourceHeight and maxSourceHeight
    const y =
      this.settings.minSourceHeight +
      Math.random() *
        (this.settings.maxSourceHeight - this.settings.minSourceHeight)

    this.settings.sourceOffset = { x, y, z }
  }

  calculateDestOffset() {
    // Start from sourceOffset
    const sourceX = this.settings.sourceOffset.x
    const sourceY = this.settings.sourceOffset.y
    const sourceZ = this.settings.sourceOffset.z

    // Calculate direction from center (0,0,0) to source
    const direction = new THREE.Vector3(sourceX, sourceY, sourceZ)
    const distance = direction.length()

    // Normalize direction (if not at center)
    if (distance > 0.001) {
      direction.normalize()
    } else {
      // If source is at center, use a default direction (e.g., positive x)
      direction.set(1, 0, 0)
    }

    // Generate random radius offset between minDestRadius and maxDestRadius
    const radiusOffset =
      this.settings.minDestRadius +
      Math.random() *
        (this.settings.maxDestRadius - this.settings.minDestRadius)

    // Calculate x and z offsets in the direction away from center
    const xOffset = direction.x * radiusOffset
    const zOffset = direction.z * radiusOffset

    // Generate random y offset between minDestHeight and maxDestHeight
    const yOffset =
      this.settings.minDestHeight +
      Math.random() *
        (this.settings.maxDestHeight - this.settings.minDestHeight)

    // Add offsets to source position (extending outward from center)
    this.settings.destOffset = {
      x: sourceX + xOffset,
      y: sourceY + yOffset,
      z: sourceZ + zOffset,
    }
  }

  createMesh() {
    const raySettings = {
      sourceOffset: new THREE.Vector3(
        this.settings.sourceOffset.x,
        this.settings.sourceOffset.y,
        this.settings.sourceOffset.z,
      ),
      destOffset: new THREE.Vector3(
        this.settings.destOffset.x,
        this.settings.destOffset.y,
        this.settings.destOffset.z,
      ),
      radius0: this.settings.radius0,
      radius1: this.settings.radius1,
      minRadius: this.settings.minRadius,
      maxIterations: this.settings.maxIterations,
      isEternal: this.settings.isEternal,
      timeScale: this.settings.timeScale,
      propagationTimeFactor: this.settings.propagationTimeFactor,
      vanishingTimeFactor: this.settings.vanishingTimeFactor,
      subrayPeriod: this.settings.subrayPeriod,
      subrayDutyCycle: this.settings.subrayDutyCycle,
      maxSubrayRecursion: this.settings.maxSubrayRecursion,
      ramification: this.settings.ramification,
      recursionProbability: this.settings.recursionProbability,
      roughness: this.settings.roughness,
      straightness: this.settings.straightness,
    }

    this.lightningStrike = new LightningStrike(raySettings)
    this.lightningStrikeMaterial = new THREE.MeshBasicNodeMaterial()
    this.lightningStrikeMaterial.colorNode = Fn(() => {
      return this.settings.color
    })()
    ;(
      this.lightningStrikeMaterial as unknown as THREE.MeshPhysicalNodeMaterial
    ).emissiveNode = this.settings.emissiveColor.mul(
      this.settings.emissiveIntensity,
    )

    this.lightningStrikeMesh = new THREE.Mesh(
      this.lightningStrike,
      this.lightningStrikeMaterial,
    )

    this.scene.add(this.lightningStrikeMesh)
  }

  recreateLightning() {
    const rayParams = (this.lightningStrike as any).rayParameters

    // Update parameters that can be changed smoothly
    if (rayParams.sourceOffset) {
      rayParams.sourceOffset.set(
        this.settings.sourceOffset.x,
        this.settings.sourceOffset.y,
        this.settings.sourceOffset.z,
      )
    } else {
      rayParams.sourceOffset = new THREE.Vector3(
        this.settings.sourceOffset.x,
        this.settings.sourceOffset.y,
        this.settings.sourceOffset.z,
      )
    }

    if (rayParams.destOffset) {
      rayParams.destOffset.set(
        this.settings.destOffset.x,
        this.settings.destOffset.y,
        this.settings.destOffset.z,
      )
    } else {
      rayParams.destOffset = new THREE.Vector3(
        this.settings.destOffset.x,
        this.settings.destOffset.y,
        this.settings.destOffset.z,
      )
    }

    rayParams.radius0 = this.settings.radius0
    rayParams.radius1 = this.settings.radius1
    rayParams.minRadius = this.settings.minRadius
    rayParams.isEternal = this.settings.isEternal
    rayParams.timeScale = this.settings.timeScale
    rayParams.propagationTimeFactor = this.settings.propagationTimeFactor
    rayParams.vanishingTimeFactor = this.settings.vanishingTimeFactor
    rayParams.subrayPeriod = this.settings.subrayPeriod
    rayParams.subrayDutyCycle = this.settings.subrayDutyCycle
    rayParams.roughness = this.settings.roughness
    rayParams.straightness = this.settings.straightness

    // Trigger geometry regeneration by calling updateMesh with current time
    ;(this.lightningStrike as any).updateMesh(this.currentTime)

    // Parameters that require recreation (check if changed)
    const needsRecreation =
      rayParams.maxIterations !== this.settings.maxIterations ||
      rayParams.maxSubrayRecursion !== this.settings.maxSubrayRecursion ||
      rayParams.ramification !== this.settings.ramification ||
      rayParams.recursionProbability !== this.settings.recursionProbability

    if (needsRecreation) {
      // Remove old mesh
      this.scene.remove(this.lightningStrikeMesh)
      this.lightningStrike.dispose()

      // Create new lightning with updated settings
      const raySettings = {
        sourceOffset: new THREE.Vector3(
          this.settings.sourceOffset.x,
          this.settings.sourceOffset.y,
          this.settings.sourceOffset.z,
        ),
        destOffset: new THREE.Vector3(
          this.settings.destOffset.x,
          this.settings.destOffset.y,
          this.settings.destOffset.z,
        ),
        radius0: this.settings.radius0,
        radius1: this.settings.radius1,
        minRadius: this.settings.minRadius,
        maxIterations: this.settings.maxIterations,
        isEternal: this.settings.isEternal,
        timeScale: this.settings.timeScale,
        propagationTimeFactor: this.settings.propagationTimeFactor,
        vanishingTimeFactor: this.settings.vanishingTimeFactor,
        subrayPeriod: this.settings.subrayPeriod,
        subrayDutyCycle: this.settings.subrayDutyCycle,
        maxSubrayRecursion: this.settings.maxSubrayRecursion,
        ramification: this.settings.ramification,
        recursionProbability: this.settings.recursionProbability,
        roughness: this.settings.roughness,
        straightness: this.settings.straightness,
      }

      this.lightningStrike = new LightningStrike(raySettings)
      this.lightningStrikeMesh.geometry = this.lightningStrike

      // Add the mesh back to the scene
      this.scene.add(this.lightningStrikeMesh)
    }
  }

  update(time: number) {
    this.currentTime = time
    this.lightningStrike.update(time)
  }
}
